import React, { Fragment, useEffect, useState } from "react";
// store
import { PAGINATION_LIMIT, API } from "../../../../store/config";
import { APIRequest } from "../../../../store/services/helpers/requests";
import moment from "moment";
import "moment/locale/sk";
// components
import { Table, Pagination, Icon, Button, Form, Checkbox, Message, Label } from "semantic-ui-react";
import { DateInput } from "semantic-ui-calendar-react";

import { toast } from '../../../../components/others/ToastContainer';

// component
function RewardsTable({ rewards }) {
    const [tableData, setTableData] = useState(
        rewards.slice(0, PAGINATION_LIMIT)
    );
    const [selectedRewards, setSelectedRewards] = useState([]);
    const [bulkPaymentDate, setBulkPaymentDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", content: "" });

    // set data to table
    useEffect(() => {
        setTableData(rewards.slice(0, PAGINATION_LIMIT));
    }, [rewards]);

    const handleChangePage = (event, data) => {
        setTableData(
            rewards.slice(
                data.activePage * PAGINATION_LIMIT - PAGINATION_LIMIT,
                data.activePage * PAGINATION_LIMIT
            )
        );
    };

    const handleSelectReward = (rewardId, checked) => {
        if (checked) {
            setSelectedRewards([...selectedRewards, rewardId]);
        } else {
            setSelectedRewards(selectedRewards.filter(id => id !== rewardId));
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const unpaidRewards = tableData.filter(reward => !reward.is_paid).map(reward => reward.id);
            setSelectedRewards(unpaidRewards);
        } else {
            setSelectedRewards([]);
        }
    };


    const updateBulkRewardsPaidStatus = async (paidWhen = null) => {
        if (selectedRewards.length === 0) {
            toast({ type: "warning", message: "Vyberte aspoň jeden záznam." });
            return;
        }

        setLoading(true);
        let successCount = 0;
        let errorCount = 0;

        if (paidWhen) {
            paidWhen = moment(paidWhen, "DD.MM.YYYY").format("YYYY-MM-DD");
        }

        for (const rewardId of selectedRewards) {
            const response = await APIRequest({
                url: `${API.GET_REWARDS}${rewardId}/`,
                method: 'PUT',
                data: {
                    paid_when: paidWhen || undefined
                },
                private: true
            });

            if (response.status === 200) {
                successCount++;
            } else {
                errorCount++;
            }
        }

        if (errorCount === 0) {
            toast({ type: "success", message: `Úspešne aktualizované ${successCount} záznamov!` });
        } else {
            toast({ type: "warning", message: `Aktualizované ${successCount} záznamov, ${errorCount} zlyhalo.` });
        }

        setSelectedRewards([]);
        setBulkPaymentDate("");
        setLoading(false);
    };

    const unpaidRewardsCount = tableData.filter(reward => !reward.is_paid).length;
    const isAllSelected = unpaidRewardsCount > 0 && selectedRewards.length === unpaidRewardsCount;

    // template
    return (
        <Fragment>
            {message.content && (
                <Message
                    positive={message.type === "success"}
                    negative={message.type === "error"}
                    warning={message.type === "warning"}
                    onDismiss={() => setMessage({ type: "", content: "" })}
                >
                    {message.content}
                </Message>
            )}

            {selectedRewards.length > 0 && (
                <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#f0f0f0", borderRadius: "4px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <Form style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            <DateInput
                                style={{ marginTop: "1rem" }}
                                placeholder="Dátum vyplatenia"
                                value={bulkPaymentDate}
                                onChange={(e, { value }) => setBulkPaymentDate(value)}
                            />
                            <Button
                                color="green"
                                disabled={!bulkPaymentDate}
                                loading={loading}
                                onClick={() => updateBulkRewardsPaidStatus(bulkPaymentDate || null)}
                            >
                                Označiť ako vyplatené
                            </Button>
                            <Button
                                basic
                                onClick={() => setSelectedRewards([])}
                            >
                                Zrušiť výber
                            </Button>
                        </Form>
                        <span><strong>{selectedRewards.length}</strong> záznamov vybraných</span>
                    </div>
                </div>
            )}

            <Table striped unstackable style={{ overflow: "auto" }}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={selectedRewards.length > 0 && !isAllSelected}
                                onChange={(e, { checked }) => handleSelectAll(checked)}
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>Číslo odmeny</Table.HeaderCell>
                        <Table.HeaderCell>Zadávateľ</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Platca DPH</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Obdobie</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Zárobok</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Provízia</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Vyplatená</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Dátum vyplatenia</Table.HeaderCell>
                        <Table.HeaderCell textAlign="left">Akcie</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {tableData.length === 0 &&
                        <Table.Row>
                            <Table.Cell colSpan={10} style={{ textAlign: "center", fontWeight: "bold" }}>
                                Vo zvolenom období neboli najdené žiadne záznamy
                            </Table.Cell>
                        </Table.Row>
                    }
                    {tableData.map((reward) => (
                        <Table.Row key={reward.id}>
                            <Table.Cell>
                                {!reward.is_paid && (
                                    <Checkbox
                                        checked={selectedRewards.includes(reward.id)}
                                        onChange={(e, { checked }) => handleSelectReward(reward.id, checked)}
                                    />
                                )}
                            </Table.Cell>
                            <Table.Cell>
                                <span> {reward.reward_number} </span>
                            </Table.Cell>
                            <Table.Cell>
                                <h5 style={{ marginBottom: 0 }}>
                                    {reward.provider_name}
                                </h5>
                                <span style={{ opacity: 0.8 }}> {reward.provider_address} </span>
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                {reward.commission.taxpayer ? "Áno" : "Nie"}
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                {reward.dates.date_from} - {reward.dates.date_to}
                            </Table.Cell>
                            <Table.Cell textAlign="left" style={{ color: "green" }}>
                                + {reward.commission.provider_profit} EUR
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                {reward.commission.commission} EUR
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                {reward.is_paid ? (
                                    <Label color="green" size="tiny">Áno</Label>
                                ) : (
                                    <Label color="red" size="tiny">Nie</Label>
                                )}
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                {reward.paid_when ? moment(reward.paid_when).format("DD.MM.YYYY") : "-"}
                            </Table.Cell>
                            <Table.Cell textAlign="left">
                                <a href={API.GET_REWARDS + reward.id + "/docs"} target="_blank" rel="noopener noreferrer">
                                    <Icon name="file pdf" style={{ color: "red", fontSize: "1.2rem" }} />
                                    <span style={{ fontWeight: "bold", color: "black" }}>Zobraziť</span>
                                </a>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            <Pagination
                defaultActivePage={1}
                totalPages={Math.ceil(
                    rewards.length / PAGINATION_LIMIT
                )}
                onPageChange={handleChangePage}
                boundaryRange={0}
                siblingRange={0}
            />
        </Fragment>
    );
}

export default RewardsTable;
